// libraries
import { useCallback, useEffect, useState, useMemo } from 'react'
import { startCase, toLower } from 'lodash'
import Dinero from 'dinero.js'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { Box, Typography, Stack, Tooltip } from '@mui/material'
import LockIcon from '@mui/icons-material/Lock'
import { styled } from '@mui/system'
import { useGridApiRef } from '@mui/x-data-grid-pro'

// components
import BasicDataGrid from 'components/generic/datagrid'
import EditableCell from 'components/generic/datagrid/cells/EditableCell'
import InputFormat from 'components/generic/inputs/formatting/InputFormat'

// queries and mutations
import { useGetPriceSheetQuery } from 'services/priceSheets'
import { useGetMaterialTypesQuery } from 'services/materialTypes'

// utils and helpers
import { setDecimalPrecision, formatNumber, checkIfDateIsLocked, generateLockTooltip } from 'utils/helpers'
import { calculateTotalWeight } from 'utils/data-manipulation/common'
import { INVOICE_WEIGHT_MODES, MEASURE_UNIT_ARRAY } from 'utils/constants'
import { calculateRowNetValue } from 'components/containers/outbound-tickets/utils'
import { useAppSelector } from 'utils/custom-hooks/useTypedReduxStore'
import { roundTo, formatMaterialName } from 'utils/data-manipulation/valueFormatter'
import { ROUNDING_PRECISIONS } from 'utils/constants'

const GridStyling = styled('div')(({ theme }) => ({
  '& .MuiDataGrid-root': {
    border: 'none',
    // remove header padding to align headers with column values
    '& .MuiDataGrid-columnHeaderTitleContainer': {
      padding: theme.spacing(0),
    },
    '& .MuiDataGrid-columnHeader': {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
    '& .MuiDataGrid-cell': {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
    '& .MuiDataGrid-columnSeparator': {
      visibility: 'hidden',
    },
    // replace default hover / active-row styling from datagrid
    '& .MuiDataGrid-row': {
      '&.total-row': {
        backgroundColor: theme.palette.info.lightBg,
        fontWeight: '500',
        '&:hover': {
          backgroundColor: theme.palette.info.lightBg,
        },
      },
      '&:not(.total-row)': {
        backgroundColor: 'inherit',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.08)',
        },
      },
    },
    '& .MuiDataGrid-columnHeader:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
    '& .MuiCircularProgress-root': {
      zIndex: 1,
    },
  },
}))

interface AddToMaterialsDictProps {
  materialTypeId: string
  orderEntryId?: string
  gross: number
  tare: number
  net: number
  itemCount?: number
  isLoose: boolean
}

const InvoiceTicketTable = ({
  ticketInfo,
  updateTotalRowData,
  invoiceData,
  salesOrderData,
  setTicketsAreLoading,
  weightMode,
  isEditMode,
  control,
  setValue,
  pricePrepend,
  setCachedMaterialTypesData,
  apiRefDict,
  setApiRefDict,
}) => {
  const apiRef = useGridApiRef()
  const { defaultYardUm, settings } = useAppSelector(state => state.settings)
  const { decimalPrecision, disableUmDropdowns } = settings?.purchaseSettings?.inboundTickets || {}
  const { lockAccountingPeriod } = settings?.overviewSettings?.overview || {}

  const { enablePullApartFeatures, enableTransferTicketsConsumerWeightsFF, enableLockAccountingV2 } = useAppSelector(
    state => state.featureFlags?.variables || {},
  )

  const isInLockedAccountingPeriodV2 = useMemo(() => {
    if (!enableLockAccountingV2) return false
    if (!ticketInfo || !lockAccountingPeriod || !lockAccountingPeriod.isLockedForAccounting) return false
    return checkIfDateIsLocked(lockAccountingPeriod, ticketInfo.effectiveDate)
  }, [lockAccountingPeriod, ticketInfo, enableLockAccountingV2])

  const { data: priceSheetData, isFetching: isFetchingPriceSheet } = useGetPriceSheetQuery(
    { priceSheetId: salesOrderData?.priceSheetId },
    { skip: !salesOrderData?.priceSheetId },
  )

  const materialTypeIds = useMemo(() => {
    if (ticketInfo) {
      const set = new Set()
      ticketInfo.packingLists?.forEach(packingList => {
        set.add(packingList.finishedGoods?.[0]?.materialTypeId || '')
      })
      ticketInfo.finishedGoods?.forEach(finishedGood => {
        set.add(finishedGood.materialTypeId)
      })
      ticketInfo.materialWeights?.forEach(material => {
        set.add(material.materialTypeId)
      })
      return Array.from(set)
    }
    return []
  }, [ticketInfo])

  const { data: materialTypesData, isFetching: isFetchingMaterialTypes } = useGetMaterialTypesQuery(
    { filter: JSON.stringify({ materialTypeIds }) },
    { skip: materialTypeIds.length === 0 },
  )

  const [materials, setMaterials] = useState<any[]>([])
  const isLoading = isFetchingPriceSheet || isFetchingMaterialTypes

  useEffect(() => {
    setTicketsAreLoading(isLoading)
  }, [isLoading])

  useEffect(() => {
    if (!ticketInfo.id) return

    if (!apiRefDict[ticketInfo.id]) {
      setApiRefDict(prev => ({
        ...prev,
        [ticketInfo.id]: apiRef,
      }))
    }
  }, [apiRef, ticketInfo.id])

  useEffect(() => {
    if (materialTypesData) {
      setCachedMaterialTypesData(prevState => {
        const newState = { ...prevState }
        materialTypesData?.Items?.forEach(materialType => {
          newState[materialType.id] = materialType
        })
        return newState
      })
    }
  }, [materialTypesData])

  useEffect(() => {
    if (invoiceData && ticketInfo && priceSheetData && materialTypesData) {
      const materialsDict = {}
      const addToMaterialsDict = ({
        materialTypeId,
        orderEntryId,
        gross,
        tare,
        net,
        itemCount = 0,
        isLoose,
      }: AddToMaterialsDictProps) => {
        const key = `${materialTypeId}${orderEntryId ?? ''}`
        if (!materialsDict[key]) {
          // note:
          // we are storing allMaterialsNetArray so that row totals can be individually calculated/rounded when using yard weights
          // looseMaterialsNetArray is similar except it is only for loose materials
          // we only use looseMaterialsNetArray for Pull Apart, when the price unit is EACH - see calculateRowNetValue function)
          materialsDict[key] = {
            yardGross: gross,
            yardTare: tare,
            yardNet: net,
            materialTypeId,
            orderEntryId,
            allMaterialsNetArray: [net],
            looseMaterialsNetArray: isLoose ? [net] : [],
            itemCount,
          }
        } else {
          materialsDict[key].yardGross += gross
          materialsDict[key].yardTare += tare
          materialsDict[key].yardNet += net
          materialsDict[key].allMaterialsNetArray = [...materialsDict[key].allMaterialsNetArray, net]
          materialsDict[key].itemCount += itemCount
          if (isLoose) {
            materialsDict[key].looseMaterialsNetArray = [...materialsDict[key].looseMaterialsNetArray, net]
          }
        }
      }

      ticketInfo.packingLists?.forEach(packingList => {
        const { materialTypeId } = packingList.finishedGoods[0]
        packingList.finishedGoods.forEach(finishedGood => {
          const {
            weight: { gross, tare, net },
            orderEntryId,
            itemCount,
          } = finishedGood
          addToMaterialsDict({ materialTypeId, orderEntryId, gross, tare, net, itemCount, isLoose: false })
        })
      })

      ticketInfo.finishedGoods?.forEach(finishedGood => {
        const {
          weight: { gross, tare, net },
          materialTypeId,
          orderEntryId,
          itemCount,
        } = finishedGood
        addToMaterialsDict({ materialTypeId, orderEntryId, gross, tare, net, itemCount, isLoose: false })
      })

      ticketInfo.materialWeights?.forEach(material => {
        const { materialTypeId, orderEntryId, gross, tare, weight } = material
        addToMaterialsDict({ materialTypeId, orderEntryId, gross, tare, net: weight, isLoose: true })
      })

      // convert price sheet data into a dict (price, um, and materialName)
      const priceDict = {}
      // Keep track of how many time each material type appears
      const ocurrencesMap = new Map<string, number>()

      priceSheetData.entriesObj?.forEach(priceEntry => {
        const key = `${priceEntry.materialTypeId}${priceEntry.id ?? ''}`
        ocurrencesMap.set(priceEntry.materialTypeId, (ocurrencesMap.get(priceEntry.materialTypeId) ?? 0) + 1)

        priceDict[key] = {
          yardPrice: Dinero({ ...priceEntry?.sellPrice?.price }).toUnit(),
          yardUm: priceEntry.weightUnits || defaultYardUm,
          materialTypeId: priceEntry.materialTypeId,
          orderEntryId: priceEntry.id,
          materialName: formatMaterialName({
            commonName: priceEntry.materialName,
            code: priceEntry.materialCode,
            overrideName: priceEntry.overrideName,
          }),
        }
      })

      // For materials without duplicates, add them to the map without the entry ID
      priceSheetData.entriesObj.forEach(entry => {
        const key = `${entry.materialTypeId}${entry.id}`
        if (ocurrencesMap.get(entry.materialTypeId) === 1) {
          priceDict[entry.materialTypeId] = priceDict[key]
        }
      })

      // add price, um, and materialName information to materials
      const materialsDictArray: any[] = Object.entries(materialsDict)
      const formattedMaterials = materialsDictArray.map(([key, material]) => {
        const priceInformation = priceDict[key]
        /**
         * TODO: we need to cover the case where there may be multiple materials with null order entry IDs on a ticket
         */
        const consumerWeight =
          invoiceData?.consumerWeights?.find(
            entry =>
              entry.outboundTicketId === ticketInfo.id &&
              entry.materialTypeId === material.materialTypeId &&
              (material.orderEntryId ? entry.orderEntryId === material.orderEntryId : true),
          ) || {}
        const {
          materialNameOverride,
          consumerItemCount,
          consumerGross,
          consumerTare,
          consumerNetWeight,
          consumerPrice,
          um,
        } = consumerWeight
        const formattedMaterial: any = {
          id: key,
          ...material,
          consumerMaterialName: materialNameOverride ?? null,
          consumerItemCount: consumerItemCount || consumerItemCount === 0 ? consumerItemCount : material?.itemCount,
          consumerGross: consumerGross || consumerGross === 0 ? consumerGross : material?.yardGross,
          consumerTare: consumerTare || consumerTare === 0 ? consumerTare : material?.yardTare,
          consumerNet: consumerNetWeight || consumerNetWeight === 0 ? consumerNetWeight : material?.yardNet,
          consumerPrice: consumerPrice || consumerPrice === 0 ? consumerPrice : priceInformation?.yardPrice,
          consumerUm: um || priceInformation?.yardUm,
        }

        if (priceInformation) {
          setValue(`consumerWeights.${ticketInfo.id}.${key}`, {
            materialTypeId: formattedMaterial.materialTypeId,
            orderEntryId: formattedMaterial.orderEntryId,
            consumerItemCount: formattedMaterial.consumerItemCount,
            consumerGross: formattedMaterial.consumerGross,
            consumerTare: formattedMaterial.consumerTare,
            consumerPrice: formattedMaterial.consumerPrice,
            consumerUm: formattedMaterial.consumerUm,
            consumerMaterialName: formattedMaterial.consumerMaterialName,
            id: consumerWeight?.id,
          })
          return {
            ...formattedMaterial,
            ...priceInformation,
          }
        }
        const defaultMaterialType = materialTypesData.Items?.find(materialType => materialType.id === key)
        const defaultPriceInformation = {
          materialName: defaultMaterialType ? `${defaultMaterialType?.commonName} (${defaultMaterialType?.code})` : '',
          yardUm: defaultMaterialType?.defaultPriceEntry?.weightUnits || defaultYardUm,
          consumerPrice: formattedMaterial.consumerPrice,
          consumerUm: formattedMaterial.consumerUm,
        }
        setValue(`consumerWeights.${ticketInfo.id}.${key}`, {
          materialTypeId: formattedMaterial.materialTypeId,
          orderEntryId: formattedMaterial.orderEntryId,
          consumerItemCount: formattedMaterial.consumerItemCount,
          consumerMaterialName: formattedMaterial.consumerMaterialName,
          consumerGross: formattedMaterial.consumerGross,
          consumerTare: formattedMaterial.consumerTare,
          consumerPrice: defaultPriceInformation.consumerPrice,
          consumerUm: defaultPriceInformation.consumerUm,
          id: consumerWeight?.id,
        })
        return {
          ...formattedMaterial,
          ...defaultPriceInformation,
        }
      })

      /**
       * Add freight charge row if there's a freight cost
       */
      if (ticketInfo.freightCost?.chargeBack) {
        formattedMaterials.push({
          id: 'freight',
          materialName: 'Freight Charge',
          yardGross: 0,
          yardTare: 0,
          yardNet: 0,
          consumerGross: 0,
          consumerTare: 0,
          consumerNet: 0,
          itemCount: 0,
          consumerItemCount: 0,
          yardPrice: 0,
          consumerPrice: 0,
          yardUm: defaultYardUm,
          consumerUm: defaultYardUm,
          value: ticketInfo.freightCost.chargeBack,
        })
      }

      setMaterials(formattedMaterials)
    }
  }, [invoiceData, ticketInfo, priceSheetData, materialTypesData, isEditMode, defaultYardUm])

  const createTotalRow = () => {
    apiRef.current?.updateRows([{ id: 'total', code: 'Total' }])
    const rows = Array.from(apiRef.current?.getRowModels().values() || []).slice(0, -1)

    const yardGross = calculateTotalWeight(
      rows.filter(row => row.id !== 'freight'),
      { fieldName: 'yardGross' },
    )
    const yardTare = calculateTotalWeight(
      rows.filter(row => row.id !== 'freight'),
      { fieldName: 'yardTare' },
    )
    const yardNet = calculateTotalWeight(
      rows.filter(row => row.id !== 'freight'),
      { fieldName: 'yardNet' },
    )
    const itemCount = calculateTotalWeight(
      rows.filter(row => row.id !== 'freight'),
      { fieldName: 'itemCount' },
    )
    const consumerGross = calculateTotalWeight(
      rows.filter(row => row.id !== 'freight'),
      { fieldName: 'consumerGross' },
    )
    const consumerTare = calculateTotalWeight(
      rows.filter(row => row.id !== 'freight'),
      { fieldName: 'consumerTare' },
    )
    const consumerNet = calculateTotalWeight(
      rows.filter(row => row.id !== 'freight'),
      { fieldName: 'consumerNet' },
    )
    const consumerItemCount = calculateTotalWeight(
      rows.filter(row => row.id !== 'freight'),
      { fieldName: 'consumerItemCount' },
    )

    const value = rows.reduce((aggregate, row) => {
      if (row.id === 'freight') {
        return aggregate + (row.value || 0)
      }
      const rowNetValue = calculateRowNetValue({ weightMode, row, enablePullApartFeatures })
      return aggregate + rowNetValue
    }, 0)

    const roundedValue = roundTo(value, ROUNDING_PRECISIONS.COST)

    apiRef.current?.updateRows([
      {
        id: 'total',
        materialName: 'Total',
        itemCount,
        yardGross,
        yardTare,
        yardNet,
        consumerItemCount,
        consumerGross,
        consumerTare,
        consumerNet,
        value: roundedValue,
      },
    ])

    updateTotalRowData(ticketInfo?.id, {
      grossWeight: weightMode === INVOICE_WEIGHT_MODES.YARD ? yardGross : consumerGross,
      tareWeight: weightMode === INVOICE_WEIGHT_MODES.YARD ? yardTare : consumerTare,
      grossValue: roundedValue,
      tareValue: 0,
      freightValue: ticketInfo?.freightCost?.chargeBack || 0,
    })
  }

  useEffect(() => {
    createTotalRow()
  })

  const onFirstRowAdd = useCallback(() => {
    setTimeout(createTotalRow, 200)
  }, [apiRef])

  const materialColumns = useMemo(() => {
    const columns = [
      {
        field: 'materialName',
        headerName: 'Material Name',
        flex: 2,
        headerAlign: 'left',
        align: 'left',
        sortable: false,
        renderCell: params => {
          const { row = {} } = params,
            { materialName, consumerMaterialName } = row
          const value = weightMode === INVOICE_WEIGHT_MODES.YARD ? materialName : consumerMaterialName ?? ''
          if (row.id === 'total' || row.id === 'freight') {
            return row.materialName
          }
          if (
            isEditMode &&
            ticketInfo?.invoiceStatuses?.[0]?.status !== 'PAID' &&
            weightMode == INVOICE_WEIGHT_MODES.CONSUMER &&
            (enableTransferTicketsConsumerWeightsFF ? !invoiceData?.lockConsumerWeights : true)
          ) {
            return (
              <EditableCell
                name={`consumerWeights.${ticketInfo.id}.${row.id}.consumerMaterialName`}
                control={control}
                placeholder={row.materialName}
                defaultValue={value || ''}
                isEdit
                size="small"
                InputProps={{
                  inputProps: {
                    onChange: event => {
                      apiRef.current?.updateRows([
                        {
                          id: row.id,
                          consumerMaterialName: event.target.value.trim() || null,
                        },
                      ])
                    },
                  },
                }}
              />
            )
          } else {
            return value || row.materialName
          }
        },
      },
      {
        field: 'itemCount',
        headerName: 'Count',
        flex: 1,
        headerAlign: 'right',
        align: 'right',
        type: 'number',
        renderCell: params => {
          const { row = {} } = params
          if (row.id === 'freight') return ''
          const { id, itemCount = 0, consumerItemCount = 0 } = row
          const value = weightMode === INVOICE_WEIGHT_MODES.YARD ? itemCount : consumerItemCount
          if (
            !isEditMode ||
            id === 'total' ||
            weightMode === INVOICE_WEIGHT_MODES.YARD ||
            invoiceData?.invoiceStatuses?.[0]?.status === 'PAID' ||
            (enableTransferTicketsConsumerWeightsFF ? invoiceData?.lockConsumerWeights : false)
          ) {
            return value
          }
          return (
            <EditableCell
              name={`consumerWeights.${ticketInfo.id}.${id}.consumerItemCount`}
              control={control}
              defaultValue={value || value === 0 ? String(value) : ''}
              isEdit
              size="small"
              InputProps={{
                inputComponent: InputFormat,
                inputProps: {
                  name: 'consumerItemCount',
                  inputType: 'number',
                  decimalScale: 0,
                  allowNegative: false,
                  onFocus: event => setTimeout(() => event.target.focus(), 100),
                  onChange: event => {
                    apiRef.current?.updateRows([
                      {
                        id,
                        consumerItemCount: event.target.value,
                      },
                    ])
                    createTotalRow()
                  },
                  style: {
                    fontSize: '0.875rem',
                    textAlign: 'right',
                  },
                },
              }}
            />
          )
        },
        sortable: false,
      },
      {
        field: 'gross',
        headerName: `${weightMode === INVOICE_WEIGHT_MODES.YARD ? 'Yard' : 'Consumer'} Gross`,
        flex: 1,
        headerAlign: 'right',
        align: 'right',
        type: 'number',
        renderCell: params => {
          const { row = {} } = params
          if (row.id === 'freight') return ''
          const { id, yardGross = 0, consumerGross = 0, consumerTare = 0 } = row
          const value = weightMode === INVOICE_WEIGHT_MODES.YARD ? yardGross : consumerGross
          if (
            !isEditMode ||
            id === 'total' ||
            weightMode === INVOICE_WEIGHT_MODES.YARD ||
            invoiceData?.invoiceStatuses?.[0]?.status === 'PAID' ||
            (enableTransferTicketsConsumerWeightsFF ? invoiceData?.lockConsumerWeights : false)
          ) {
            return `${formatNumber(value)} ${defaultYardUm}`
          }
          return (
            <EditableCell
              name={`consumerWeights.${ticketInfo.id}.${id}.consumerGross`}
              control={control}
              defaultValue={value || value === 0 ? String(value) : ''}
              isEdit
              size="small"
              InputProps={{
                inputComponent: InputFormat,
                inputProps: {
                  name: 'gross',
                  inputType: 'number',
                  decimalScale: 0,
                  onFocus: event => setTimeout(() => event.target.focus(), 100),
                  onChange: event => {
                    apiRef.current?.updateRows([
                      {
                        id,
                        consumerGross: event.target.value,
                        consumerNet: event.target.value - consumerTare,
                      },
                    ])
                    createTotalRow()
                  },
                  style: {
                    fontSize: '0.875rem',
                    textAlign: 'right',
                  },
                },
              }}
            />
          )
        },
        sortable: false,
      },
      {
        field: 'tare',
        headerName: `${weightMode === INVOICE_WEIGHT_MODES.YARD ? 'Yard' : 'Consumer'} Tare`,
        flex: 1,
        headerAlign: 'right',
        align: 'right',
        type: 'number',
        renderCell: params => {
          const { row = {} } = params
          if (row.id === 'freight') return ''
          const { id, yardTare = 0, consumerGross = 0, consumerTare = 0 } = row
          const value = weightMode === INVOICE_WEIGHT_MODES.YARD ? yardTare : consumerTare
          if (
            !isEditMode ||
            id === 'total' ||
            weightMode === INVOICE_WEIGHT_MODES.YARD ||
            invoiceData?.invoiceStatuses?.[0]?.status === 'PAID' ||
            (enableTransferTicketsConsumerWeightsFF ? invoiceData?.lockConsumerWeights : false)
          ) {
            return `${formatNumber(value)} ${defaultYardUm}`
          }
          return (
            <EditableCell
              name={`consumerWeights.${ticketInfo.id}.${id}.consumerTare`}
              control={control}
              defaultValue={value || value === 0 ? String(value) : ''}
              isEdit
              size="small"
              InputProps={{
                inputComponent: InputFormat,
                inputProps: {
                  name: 'tare',
                  inputType: 'number',
                  decimalScale: 0,
                  onFocus: event => setTimeout(() => event.target.focus(), 100),
                  onChange: event => {
                    apiRef.current?.updateRows([
                      {
                        id,
                        consumerTare: event.target.value,
                        consumerNet: consumerGross - event.target.value,
                      },
                    ])
                    createTotalRow()
                  },
                  style: {
                    fontSize: '0.875rem',
                    textAlign: 'right',
                  },
                },
              }}
            />
          )
        },
        sortable: false,
      },
      {
        field: 'net',
        headerName: `${weightMode === INVOICE_WEIGHT_MODES.YARD ? 'Yard' : 'Consumer'} Net`,
        flex: 1,
        headerAlign: 'right',
        align: 'right',
        type: 'number',
        renderCell: params => {
          const { row = {} } = params
          if (row.id === 'freight') return ''
          const { yardNet = 0, consumerNet = 0 } = row
          const value = weightMode === INVOICE_WEIGHT_MODES.YARD ? yardNet : consumerNet
          return `${formatNumber(value)} ${defaultYardUm}`
        },
        sortable: false,
      },
      {
        field: 'price',
        headerName: `${weightMode === INVOICE_WEIGHT_MODES.YARD ? 'Yard' : 'Consumer'} Price${disableUmDropdowns ? '/UM' : ''}`,
        flex: 1,
        headerAlign: 'right',
        align: 'right',
        type: 'number',
        renderCell: params => {
          const { row = {} } = params
          if (row.id === 'freight') return ''
          const { id, yardPrice = 0, consumerPrice = 0 } = row
          const price = weightMode === INVOICE_WEIGHT_MODES.YARD ? yardPrice : consumerPrice
          if (id === 'total') return ''
          if (
            !isEditMode ||
            weightMode === INVOICE_WEIGHT_MODES.YARD ||
            invoiceData?.invoiceStatuses?.[0]?.status === 'PAID' ||
            (enableTransferTicketsConsumerWeightsFF ? invoiceData?.lockConsumerWeights : false)
          ) {
            return `${pricePrepend}${setDecimalPrecision(price, { decimalPrecisionOverride: decimalPrecision })}`
          }
          return (
            <EditableCell
              name={`consumerWeights.${ticketInfo.id}.${id}.consumerPrice`}
              control={control}
              defaultValue={price || price === 0 ? String(price) : ''}
              isEdit
              size="small"
              InputProps={{
                inputComponent: InputFormat,
                inputProps: {
                  name: 'price',
                  inputType: 'price',
                  pricePrepend,
                  decimalScale: decimalPrecision,
                  onFocus: event => setTimeout(() => event.target.focus(), 100),
                  onChange: event => {
                    apiRef.current?.updateRows([
                      {
                        id,
                        consumerPrice: event.target.value,
                      },
                    ])
                    createTotalRow()
                  },
                  style: {
                    fontSize: '0.875rem',
                    textAlign: 'right',
                  },
                },
              }}
            />
          )
        },
        sortable: false,
      },
      {
        field: 'slash',
        headerName: '/',
        headerAlign: 'center',
        align: 'center',
        width: 4,
        renderCell: ({ row }) => (row?.id === 'total' || row?.id === 'freight' ? '' : '/'),
        sortable: false,
      },
      {
        field: 'um',
        headerName: 'UM',
        flex: 1,
        headerAlign: 'left',
        align: 'left',
        renderCell: params => {
          const { row = {} } = params
          if (row.id === 'freight') return ''
          const { id, yardUm, consumerUm } = row
          const um = weightMode === INVOICE_WEIGHT_MODES.YARD ? yardUm : consumerUm
          if (id === 'total') return ''
          if (
            !isEditMode ||
            weightMode === INVOICE_WEIGHT_MODES.YARD ||
            invoiceData?.invoiceStatuses?.[0]?.status === 'PAID' ||
            (enableTransferTicketsConsumerWeightsFF ? invoiceData?.lockConsumerWeights : false)
          ) {
            return um?.toUpperCase()
          }
          return (
            <EditableCell
              name={`consumerWeights.${ticketInfo.id}.${id}.consumerUm`}
              label=""
              control={control}
              defaultValue={um?.toLowerCase() || ''}
              cellType="SELECT"
              size="small"
              options={
                enablePullApartFeatures ? MEASURE_UNIT_ARRAY : MEASURE_UNIT_ARRAY.filter(unit => unit.id !== 'ea')
              }
              isEdit
              InputProps={{
                sx: { fontSize: '0.875rem' },
                inputProps: {
                  onChange: event => {
                    apiRef.current?.updateRows([
                      {
                        id,
                        consumerUm: event.target.value,
                      },
                    ])
                    createTotalRow()
                  },
                },
              }}
            />
          )
        },
        sortable: false,
      },
      {
        field: 'value',
        headerName: 'Value',
        flex: 1,
        headerAlign: 'right',
        align: 'right',
        valueGetter: params => {
          const { value: rowValue } = params.row
          const value = rowValue || calculateRowNetValue({ weightMode, row: params.row, enablePullApartFeatures })
          if (params.row.id === 'freight' && value < 0) {
            return `(${pricePrepend}${setDecimalPrecision(Math.abs(value), { decimalPrecisionOverride: 2 })})`
          }
          return `${pricePrepend}${setDecimalPrecision(value, { decimalPrecisionOverride: 2 })}`
        },
        sortable: false,
      },
    ]
    const filteredColumns = columns.filter(column => {
      if (disableUmDropdowns && (column.field === 'um' || column.field === 'slash')) return false
      if (!enablePullApartFeatures && column.field === 'itemCount') return false
      return true
    })
    return filteredColumns
  }, [
    pricePrepend,
    weightMode,
    isEditMode,
    invoiceData,
    defaultYardUm,
    ticketInfo,
    control,
    decimalPrecision,
    disableUmDropdowns,
    enablePullApartFeatures,
  ])

  return (
    <>
      <Box>
        <Stack direction="row" alignItems="center" mt={4} mb={1}>
          {isInLockedAccountingPeriodV2 && (
            <Tooltip title={generateLockTooltip('outbound ticket')} placement="top">
              <LockIcon color="info" fontSize="small" sx={{ mr: 1 }} />
            </Tooltip>
          )}
          <Typography variant="subtitle1">
            <Link to={`/app/sales/outbound-tickets/${ticketInfo.id}`} target="_blank">
              {`Ticket #${ticketInfo?.ticketId || ticketInfo?.tag}`}
            </Link>
          </Typography>
          <Typography ml={1} variant="caption">
            {`${startCase(toLower(ticketInfo?.status || ticketInfo?.jsonData?.status))}`}
          </Typography>
          <Typography ml={5}>Mill Scale Ticket #: {ticketInfo?.millScaleNumber}</Typography>
          <Typography ml={5}>Booking Ref #: {ticketInfo?.contractInfo?.bookingRef}</Typography>
          <Typography ml={5}>Container #: {ticketInfo?.transportationInfo?.container}</Typography>
        </Stack>
        <GridStyling>
          <BasicDataGrid
            height="auto"
            apiRef={apiRef}
            loading={isLoading}
            rows={materials}
            columns={materialColumns}
            onCellClick={(_, event) => {
              event.stopPropagation()

              Object.values(apiRefDict).forEach((ref: any) => {
                ref.current.state.focus.cell = null
              })
            }}
            disableColumnMenu
            hideFooter
            getRowClassName={params => (params.row.id === 'total' ? 'total-row' : 'normal-row')}
            onMounted={onFirstRowAdd}
          />
        </GridStyling>
      </Box>
    </>
  )
}

InvoiceTicketTable.propTypes = {
  ticketInfo: PropTypes.object.isRequired,
  updateTotalRowData: PropTypes.func.isRequired,
  invoiceData: PropTypes.object,
  salesOrderData: PropTypes.object,
  setTicketsAreLoading: PropTypes.func.isRequired,
  weightMode: PropTypes.string.isRequired,
  isEditMode: PropTypes.bool,
  control: PropTypes.object.isRequired,
  setValue: PropTypes.func,
  pricePrepend: PropTypes.string,
  setCachedMaterialTypesData: PropTypes.func.isRequired,
  apiRefDict: PropTypes.object,
  setApiRefDict: PropTypes.func,
}

InvoiceTicketTable.defaultProps = {
  invoiceData: {},
  salesOrderData: {},
  isEditMode: false,
  setValue: () => {},
  pricePrepend: '$',
  apiRefDict: {},
  setApiRefDict: () => {},
}

export default InvoiceTicketTable
